import * as Sentry from "@sentry/react";

import { getCurrentAccessToken } from "@/lib/authSession";

const API_URL = import.meta.env.VITE_API_URL || "https://api.ondiet.app";

interface ClientError {
  type: string; // lowercase [a-z0-9_-]+: substituicao, crash, network, feedback, user_diet, auth
  message: string;
  statusCode?: number;
  path?: string;
  /**
   * Erro original do `catch`, quando existir. OPCIONAL e sem efeito no
   * backend — serve só pro Sentry: só com o `Error` na mão dá pra montar
   * stack trace de verdade. Sem ele o evento ainda sobe, agrupado por
   * `type` + `path`, mas sem stack.
   *
   * Passe sempre que tiver: `catch (e) { reportClientError({ ..., cause: e }) }`
   */
  cause?: unknown;
}

/**
 * Espelha o erro no Sentry como **Issue**.
 *
 * Por que isto existe, e por que não bastava o `initSentry()`: os erros que
 * passam por aqui são **capturados**. As integrações `window.onerror` e
 * `unhandledrejection` do SDK só veem o que **escapa** — então as 141 chamadas
 * deste helper eram invisíveis pro Sentry. Medido em 2026-08-05: o projeto
 * `ondiet-frontend` tinha **1 evento na vida** (um teste manual) enquanto 4
 * erros reais de cliente chegavam no backend no mesmo dia.
 *
 * É o mesmo furo que o `ErrorNotifier.notify_error` do backend tinha, do outro
 * lado da fronteira, e a correção é simétrica.
 *
 * Agrupamento: com `cause` real, o Sentry agrupa pelo stack. Sem ele, a
 * mensagem carrega conteúdo variável (id, contagem, status) e cada ocorrência
 * viraria um issue novo — daí o fingerprint por `type` + `path`.
 *
 * Nunca lança: observabilidade não pode ser a causa do próximo erro.
 */
function espelharNoSentry(err: ClientError): void {
  try {
    Sentry.withScope((scope) => {
      scope.setTag("client_error_type", err.type);
      if (err.statusCode != null) scope.setTag("status_code", String(err.statusCode));
      if (err.path) scope.setTag("client_error_path", err.path);
      // `extra` (não `setContext`) porque só `extra` passa pelo scrub de PII
      // do `beforeSend` em sentry.ts.
      scope.setExtra("message", err.message);

      if (err.cause instanceof Error) {
        Sentry.captureException(err.cause);
      } else {
        scope.setFingerprint(["client-error", err.type, err.path ?? "sem-path"]);
        Sentry.captureMessage(`[${err.type}] ${err.message}`, "error");
      }
    });
  } catch {
    /* engolido — o reporter nunca pode quebrar a UI nem o envio pro backend */
  }
}

/**
 * Fire-and-forget error reporting to backend.
 *
 * Envia erro pra POST /api/client-error. Se falhar (rede, backend down, etc.)
 * é engolido silenciosamente — não queremos que o reporter quebre a UI.
 *
 * Backend grava em `client_errors` + dispara alerta Telegram via ErrorNotifier.
 *
 * Usar nos catch blocks de:
 * - hooks de mutação (substituição, fracionamento, feedback submit)
 * - ErrorBoundary.componentDidCatch
 * - fetches que retornam success=false com business logic error
 */
export function reportClientError(err: ClientError): void {
  // Sentry PRIMEIRO e de forma síncrona: se o `fetch` abaixo falhar (backend
  // fora, rede caída — justamente quando mais se precisa do erro), o Sentry
  // já saiu. `captureException` só enfileira, não bloqueia.
  espelharNoSentry(err);

  // IIFE async best-effort: usa o snapshot em memória do AuthProvider. Abrir um
  // getSession() aqui recriava a mesma contenção que o reporter estava tentando
  // diagnosticar e podia prolongar a rajada depois do primeiro timeout.
  void (async () => {
    try {
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      const accessToken = getCurrentAccessToken();
      if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

      await fetch(`${API_URL}/api/client-error`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          type: err.type,
          message: (err.message ?? "").slice(0, 2000),
          status_code: err.statusCode,
          path: err.path?.slice(0, 200),
          url: window.location.href.slice(0, 500),
          user_agent: navigator.userAgent.slice(0, 500),
        }),
        keepalive: true, // deixa request seguir mesmo se a página navegar
      }).catch(() => {
        /* engolido — reporter nunca quebra UI */
      });
    } catch {
      /* engolido */
    }
  })();
}
